import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";

import MainLayout from "../layouts/MainLayout";
import { CSSGrid } from "react-stonecutter";
import Button from "../components/base/Button";
import classNames from "classnames";
import Spinner from "../components/base/Spinner";
import useLocalStorage from "../hooks/useLocalStorage";
import Card from "../components/base/Card";
import CardBody from "../components/base/CardBody";

interface Connection {
  name: any;
  synergistic: any[];
}

interface Connections {
  connections: Connection[];
}

const IMAGE_WIDTH = 200;

const colors = ["🟨", "🟩", "🟦", "🟪"];

const DateSeed = () => {
  const currentDate = new Date();
  // should be year-month-day
  return `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
};

const shuffleInPlace = (array: any[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const SynergyConnect: React.FC = () => {
  const { seed } = useParams();
  const seedToUse = seed || DateSeed();

  const [loading, setLoading] = useLocalStorage(`${seedToUse}-load`, true);
  const [connections, setConnections] = useLocalStorage<Connections | null>(
    `${seedToUse}-connections`,
    null
  );
  const [grid, setGrid] = useLocalStorage<any[]>(`${seedToUse}-grid`, []);
  const [selected, setSelected] = useLocalStorage<any[]>(
    `${seedToUse}-selected`,
    []
  );
  const [guesses, setGuesses] = useLocalStorage<any[]>(
    `${seedToUse}-guesses`,
    []
  );
  const [correct, setCorrect] = useLocalStorage<any[]>(
    `${seedToUse}-correct`,
    []
  );

  const [shake, setShake] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const [divWidth, setDivWidth] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    function handleResize() {
      if (divRef.current) {
        setDivWidth(divRef.current.offsetWidth);
      }
    }

    // Set width initially
    handleResize();

    // Update width when window size changes
    window.addEventListener("resize", handleResize);

    // Clean up event listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchConnections = async () => {
      const response = await fetch(
        `https://gdekker.io/mtgconnect/${seedToUse}`
      );
      const data = await response.json();
      console.log(data);
      setConnections(data);
      setGrid(
        shuffleInPlace(
          data.connections
            .slice(0, 4)
            .map((c: { synergistic: any }) => c.synergistic)
            .flat()
        )
      );
      setLoading(false);
    };

    if (!connections) {
      fetchConnections();
    }
  }, [connections, seedToUse, setConnections, setGrid, setLoading]);

  const shuffle = useCallback(
    async (correctOverride: any | null) => {
      if (!connections) return;

      const fixed: any = [];

      const correctToUse = correctOverride || correct;

      for (const correctSelection of correctToUse) {
        const synergistic = connections.connections.find(
          (c: any) => c.name === correctSelection
        );

        if (synergistic) {
          fixed.push(...synergistic.synergistic);
        }
      }

      const toShuffle = connections.connections
        .slice(0, 4)
        .map((c: { synergistic: any }) => c.synergistic)
        .flat()
        .filter((s: any) => !fixed.some((c: any) => c.name === s.name));

      setGrid([...fixed, ...shuffleInPlace(toShuffle)]);
    },
    [connections, correct, setGrid]
  );

  const submit = useCallback(async () => {
    if (!connections) return;

    setGuesses([...guesses, selected]);

    for (let i = 0; i < 4; i++) {
      const connection = connections.connections[i];
      if (
        selected.every((name) =>
          connection.synergistic.some((s: any) => s.name === name)
        )
      ) {
        const { name } = connection;

        if (!correct.includes(name)) {
          setCorrect([...correct, connection.name]);
          shuffle([...correct, connection.name]);
          setSelected([]);
          setMessage("");
        }
        return;
      }

      // if 3 of the 4 are correct
      if (
        selected.filter((name) =>
          connection.synergistic.some((s: any) => s.name === name)
        ).length === 3
      ) {
        setMessage("Only one away...");
        return;
      }
    }

    setMessage("");
    setShake(true);
    // Remove the shake class after the animation has completed
    setTimeout(() => setShake(false), 500);
  }, [
    connections,
    correct,
    guesses,
    selected,
    setCorrect,
    setGuesses,
    setSelected,
    shuffle,
  ]);

  const width = Math.min(IMAGE_WIDTH, (divWidth - 20) / 4);
  const height = width * 1.4;

  const correctCards =
    connections?.connections
      .filter((c) => correct.includes(c.name))
      .map((c) => c.synergistic)
      .flat()
      .map((c) => c.name) || [];

  const cardNameToColorIndex = useMemo(() => {
    if (!connections) return {};

    const res: Record<string, any> = {};

    for (let i = 0; i < 4; i++) {
      const connection = connections.connections[i];
      for (const synergistic of connection.synergistic) {
        res[synergistic.name] = i;
      }
    }

    return res;
  }, [connections]);

  const firstCardsInConnection = useMemo(() => {
    if (!connections) return [];

    const res = [];

    for (let i = 0; i < 4; i++) {
      const connection = connections.connections[i];
      res.push(connection.synergistic[0].name);
    }

    return res;
  }, [connections]);

  const correctText = useMemo(() => {
    return `My results for today's Synergy Connect (${seedToUse}): \n${guesses
      .map((row) =>
        row.map((item: any) => colors[cardNameToColorIndex[item]]).join(" ")
      )
      .join(
        "\n"
      )}\r\nTry it for yourself here: https://synergyconnect.gdekker.io`;
  }, [cardNameToColorIndex, guesses, seedToUse]);

  return (
    <MainLayout>
      <div
        ref={divRef}
        className="flex w-full gap-2 flex-col items-center justify-center"
      >
        {correctCards.length < 15 ? (
          <div className="flex gap-2 items-center">
            <Button onClick={() => shuffle(null)}>Shuffle</Button>
            <Button onClick={submit} disabled={selected.length !== 4}>
              Submit
            </Button>
          </div>
        ) : (
          <div className="container flex flex-col gap-2 items-center justify-center">
            <h1 className="text-3xl font-bold">You Win!</h1>
            <p className="text-lg">You've found all the connections!</p>
            <p>
              Share your results! Copy and paste your results here.
              <br />
              <Card className="p-2 mt-2">
                <p>My results for today's Synergy Connect ({seedToUse}): </p>
                {guesses.map((row) => (
                  <p>
                    {row
                      .map((item: any) => colors[cardNameToColorIndex[item]])
                      .join(" ")}
                  </p>
                ))}
                <p>
                  Try it for yourself here:{" "}
                  <a href="https://synergyconnect.gdekker.io">
                    https://synergyconnect.gdekker.io
                  </a>
                </p>
                <Button
                  onClick={() => navigator.clipboard.writeText(correctText)}
                >
                  Copy Results
                </Button>
              </Card>
            </p>
          </div>
        )}
        {message && (
          <Card>
            <CardBody>
              <p className="text-lg">{message}</p>
            </CardBody>
          </Card>
        )}
        {loading ? (
          <Spinner size="lg" />
        ) : (
          <CSSGrid
            component="ul"
            columns={4}
            columnWidth={width}
            gutterWidth={5}
            gutterHeight={5}
            itemHeight={height}
            springConfig={{ stiffness: 170, damping: 26 }}
            enterExitStyle="Simple"
            duration={300}
            stiffness={170}
            damping={26}
            easing="ease-out"
          >
            {grid.map((item) => (
              <div key={item.imageUrl} className="relative">
                <img
                  src={item.imageUrl}
                  alt={item.imageUrl}
                  className={classNames(
                    "transition-all duration-300 border-8 rounded-lg z-100",
                    {
                      "animate-pulse": selected.includes(item.name),
                      "hover:cursor-pointer hover:opacity-70":
                        (selected.includes(item.name) || selected.length < 4) &&
                        !correctCards.includes(item.name),
                      shake: shake && selected.includes(item.name),
                      "border-yellow-400 opacity-60":
                        correctCards.includes(item.name) &&
                        cardNameToColorIndex[item.name] === 0,
                      "border-green-400 opacity-60":
                        correctCards.includes(item.name) &&
                        cardNameToColorIndex[item.name] === 1,
                      "border-blue-500 opacity-60":
                        correctCards.includes(item.name) &&
                        cardNameToColorIndex[item.name] === 2,
                      "border-purple-500 opacity-60":
                        correctCards.includes(item.name) &&
                        cardNameToColorIndex[item.name] === 3,
                    }
                  )}
                  style={{
                    width,
                    height,
                  }}
                  onClick={() => {
                    if (selected.includes(item.name)) {
                      setSelected(
                        selected.filter((name) => name !== item.name)
                      );
                    } else if (selected.length < 4) {
                      if (!connections) return;

                      if (!correctCards.includes(item.name)) {
                        setSelected([...selected, item.name]);
                      }
                    }
                  }}
                />
                {connections &&
                  firstCardsInConnection.includes(item.name) &&
                  correctCards.includes(item.name) && (
                    <div
                      className="absolute inset-0  z-10 flex items-center justify-center rounded-lg text-3xl font-bold"
                      style={{ width: width * 4 + 15, height }}
                    >
                      {
                        connections.connections[
                          firstCardsInConnection.indexOf(item.name)
                        ].name
                      }
                    </div>
                  )}
              </div>
            ))}
          </CSSGrid>
        )}
      </div>
    </MainLayout>
  );
};

export default SynergyConnect;
